import type {ReactNode} from 'react';
import React, {useState} from 'react';

import AriaModal from 'react-aria-modal';
import CSSModules from 'react-css-modules';

import {useWindowSize} from '../hooks/windowSize';
import HoverableIcon from '../HoverableIcon';

import styles from './styles.module.scss';

type FullScreenImageButtonProps = {
  image: {
    width: number;
    height: number;
    src: string;
  };
  backgroundColor?: string;
  disabled?: boolean;
  children?: ReactNode;
  editMode?: boolean;
};

const FullScreenImageButton = ({
  image,
  backgroundColor = 'transparent',
  disabled,
  children,
  editMode,
}: FullScreenImageButtonProps): JSX.Element => {
  const [showFullScreen, setShowFullScreen] = useState(false);
  const windowSize = useWindowSize();

  const getFullScreenSizes = (innerWidth: number, innerHeight: number) => {
    const {width, height} = image;
    const widthScaleFactor = width / innerWidth;
    const heightScaleFactor = height / innerHeight;
    const scaleFactor = Math.max(widthScaleFactor, heightScaleFactor);

    if (scaleFactor > 1) {
      return {width: width / scaleFactor, height: height / scaleFactor};
    } else {
      return {width, height};
    }
  };

  let className = 'fullScreenButton';
  if (editMode) {
    className += ' preview';
  }

  const showModal = () => setShowFullScreen(true);
  const hideModal = () => setShowFullScreen(false);

  const renderFullScreenButton = () => (
    <button
      type='button'
      onClick={showModal}
      styleName={className}
      disabled={disabled}
    >
      <HoverableIcon
        noHoverIcon='/resources/img/btn_fullscreen.svg'
        hoverIcon='/resources/img/btn_fullscreen_over.svg'
      />
    </button>
  );

  const renderClickableChildren = () => (
    <div onClick={!disabled ? showModal : () => {}}>{children}</div>
  );

  return (
    <div>
      <AriaModal
        mounted={showFullScreen}
        titleText='Full screen image'
        onExit={hideModal}
        getApplicationNode={() =>
          document.getElementById('appContent') as Element
        }
        underlayColor='rgba(13,0,58,0.5)'
        verticallyCenter
        focusTrapPaused
        initialFocus='#minimizeButton'
      >
        <div styleName='modalContainer'>
          <div styleName='imageContainer'>
            <img
              src={image.src}
              style={{
                backgroundColor,
                ...getFullScreenSizes(windowSize.width, windowSize.height),
              }}
            />
          </div>
          <button
            type='button'
            id='minimizeButton'
            onClick={hideModal}
            styleName='minimizeButton'
            disabled={disabled}
          >
            <HoverableIcon
              noHoverIcon='/resources/img/24_cross.svg'
              hoverIcon='/resources/img/24_cross_grey.svg'
            />
          </button>
        </div>
      </AriaModal>

      {children ? renderClickableChildren() : renderFullScreenButton()}
    </div>
  );
};

export default CSSModules(FullScreenImageButton, styles, {allowMultiple: true});
